import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const query = searchParams.get('q') || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await api(`/api/search?q=${encodeURIComponent(query)}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setResults(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [query]);

  const handleItemClick = (itemId) => {
    navigate(`/item/${itemId}`);
  };

  const handleAddToCart = (e, itemId, isOutOfStock) => {
    e.stopPropagation();
    if (!isOutOfStock) {
      addItem(itemId, 1);
    }
  };

  // Group results by market
  const groupedByMarket = results.reduce((acc, item) => {
    const marketKey = item.marketId || 'unknown';
    if (!acc[marketKey]) {
      acc[marketKey] = {
        marketId: item.marketId,
        marketName: item.marketName || 'Unknown Market',
        items: []
      };
    }
    acc[marketKey].items.push(item);
    return acc;
  }, {});

  // Sort markets and items within markets
  const marketGroups = Object.values(groupedByMarket).map(group => ({
    ...group,
    items: group.items.sort((a, b) => {
      // Sort by price (ascending), then by vendor name
      if (a.price !== b.price) {
        return a.price - b.price;
      }
      return (a.vendorName || '').localeCompare(b.vendorName || '');
    })
  }));

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold">Search Results</h1>
      
      {!query.trim() ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            Enter a search term to find items.
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-2xl text-slate-500 dark:text-slate-400">Searching…</p>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </div>
      ) : results.length === 0 ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            No items found for "{query}".
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {marketGroups.map((group) => (
            <div key={group.marketId || 'unknown'} className="flex flex-col gap-4">
              <h2 className="text-2xl font-semibold border-b-2 border-indigo-600 dark:border-indigo-400 pb-2 text-center">
                {group.marketName} ({group.items.length} {group.items.length === 1 ? 'item' : 'items'})
              </h2>
              
              <div
                className={
                  group.items.length === 1
                    ? 'w-full max-w-lg mx-auto'
                    : 'grid grid-cols-2 sm:grid-cols-3 gap-3'
                }
              >
                {group.items.map((item) => {
                  const imageUrls = item.imageUrls || [];
                  const firstImage = imageUrls.length > 0 ? imageUrls[0] : null;
                  const isOutOfStock = item.stockCount <= 0;
                  const hasLowStock = item.stockCount > 0 && item.stockCount < 10;
                  
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item.id)}
                      className="text-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden cursor-pointer hover:border-emerald-500 transition-colors"
                    >
                      <div className="aspect-square bg-slate-100 dark:bg-slate-900">
                        {firstImage ? (
                          <ImageWithPlaceholder
                            src={firstImage}
                            alt={item.title}
                            className="w-full h-full object-contain"
                            containerClassName="w-full h-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                            No image
                          </div>
                        )}
                      </div>

                      <div className="p-3 flex flex-col items-center gap-1.5">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-tight">
                          {item.title}
                        </h3>

                        <div className="flex items-center justify-center gap-1">
                          <div className="flex items-center">
                            {Array.from({ length: 5 }, (_, i) => (
                              <span
                                key={i}
                                className={`text-xs ${i < Math.round(item.averageStars || 0) ? 'text-orange-400' : 'text-slate-300 dark:text-slate-600'}`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            ({item.reviewCount || 0})
                          </span>
                        </div>

                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                            ${parseFloat(item.price).toFixed(2).split('.')[0]}
                          </span>
                          <span className="text-xs text-slate-600 dark:text-slate-400 align-super">
                            {parseFloat(item.price).toFixed(2).split('.')[1]}
                          </span>
                        </div>

                        {hasLowStock && !isOutOfStock && (
                          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                            Only {item.stockCount} left
                          </p>
                        )}
                        {isOutOfStock && (
                          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                            Out of stock
                          </p>
                        )}

                        {item.vendorName && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                            {item.vendorName}
                            {item.vendorLocation && ` · ${item.vendorLocation}`}
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={(e) => handleAddToCart(e, item.id, isOutOfStock)}
                          disabled={isOutOfStock}
                          className="mt-1 w-full h-9 rounded-md bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors font-semibold text-sm text-slate-900 flex items-center justify-center"
                        >
                          {isOutOfStock ? 'Out of Stock' : 'Add to cart'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
